import React, { FunctionComponent, ReactNode } from 'react'
import { Header } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSaleStatus,
  selectCurrentSaleAsSale,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { selectPaymentRequestSuccess } from '../../../state/slices/posState/posStateSlice'
import {
  getChangeAmountForDisplay,
  getOutstandingBalance,
  getPaymentsTotal,
  getTotalQuantityByType,
  hasOutstandingBalance,
  isAccountBalancePaymentPaid,
  isConfirmedReadyToComplete,
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccount,
  isPartialPaidSale,
  isQuote,
  isSaleTotalGreaterThanZero,
  isSaleTotalLessThanZero,
  isSaleTotalZero,
  isUnpaidSale,
  isValidCustomer,
  saleIsExchange,
} from '../../../functions/sale'
import { hasChange } from '../../../functions/payments'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { CONFIRMED_STATUSES_CLOSED } from '../../../models/sale.constants'
import { useLoyaltyManager } from '../../../ng-compat/hooks'
import { isAbpOnlyConfirmationFlow } from './abp-confirmation-flow'

const HEADER_CONTEXT = 'Sale confirmation header'
const SUBHEADER_CONTEXT = 'Sale confirmation subheader'

const HeaderTitle: FunctionComponent = () => {
  const sale = useAppSelector(selectCurrentSaleAsSale)
  const status = useAppSelector(selectSaleStatus)
  const paymentRequestSuccess = useAppSelector(selectPaymentRequestSuccess)
  const formatCurrenctValue = useFormatCurrencyValue()

  if (paymentRequestSuccess) {
    const headerTitle = paymentRequestSuccess.headerTitle
      ? paymentRequestSuccess.headerTitle
      : 'Sale saved'
    return <T _str={headerTitle} _context={HEADER_CONTEXT} />
  }

  if (isQuote(sale.status)) {
    return <T _str="Complete quote" _context={HEADER_CONTEXT} />
  }

  if (hasChange(sale.change)) {
    return (
      <T
        _str="Give {change} change"
        change={formatCurrenctValue(getChangeAmountForDisplay(sale))}
        _context={HEADER_CONTEXT}
      />
    )
  }

  // ABP-only confirmation has no sale products; show the generic paid state title.
  if (isAbpOnlyConfirmationFlow(sale)) {
    return <T _str="Payment received" _context={HEADER_CONTEXT} />
  }

  if (isSaleTotalZero(sale)) {
    if (saleIsExchange(sale)) {
      return (
        <T
          _str="{count, plural, one {Item exchanged} other {Items exchanged}}"
          _context={HEADER_CONTEXT}
          count={getTotalQuantityByType(sale, true)}
        />
      )
    }
    return <T _str="Completed" _context={HEADER_CONTEXT} />
  }

  if (
    isSaleTotalGreaterThanZero(sale) &&
    (!hasOutstandingBalance(sale) || isPartialPaidSale(sale))
  ) {
    return <T _str="Payment received" _context={HEADER_CONTEXT} />
  }

  if (isUnpaidSale(sale)) {
    return <T _str="Sale saved" _context={HEADER_CONTEXT} />
  }

  if (
    isConfirmedReadyToComplete({
      status,
      readyToComplete: sale.readyToComplete,
      isSubmittedSaleEarly: sale.isSubmittedSaleEarly,
    }) &&
    hasOutstandingBalance(sale)
  ) {
    if (isOnAccount(sale.status)) {
      const outstandingBalance = formatCurrenctValue(
        getOutstandingBalance(sale)
      )
      return (
        <T
          _str="{outstandingBalance} on account"
          _context={HEADER_CONTEXT}
          outstandingBalance={outstandingBalance}
        />
      )
    }

    if (isLayby(sale.status)) {
      return <T _str="Layaway" _context={HEADER_CONTEXT} />
    }
  }

  if (isSaleTotalLessThanZero(sale)) {
    return <T _str="Refund complete" _context={HEADER_CONTEXT} />
  }
}

const HeaderSubTitle: FunctionComponent = () => {
  const loyaltyManager = useLoyaltyManager()
  const sale = useAppSelector(selectCurrentSaleAsSale)
  const status = useAppSelector(selectSaleStatus)
  const paymentRequestSuccess = useAppSelector(selectPaymentRequestSuccess)
  const formatCurrencyValue = useFormatCurrencyValue()

  const messages: ReactNode[] = []

  if (paymentRequestSuccess) {
    messages.push(
      <span key="payment-request">
        <T
          _str="Payment request of {amount} sent"
          _context={SUBHEADER_CONTEXT}
          amount={formatCurrencyValue(
            paymentRequestSuccess.requestedAmount / 100
          )}
        />
      </span>
    )

    return (
      <>
        {messages.map((message, index) => {
          return index === messages.length - 1 ? (
            <>{message}.</>
          ) : (
            <>{message}, </>
          )
        })}
      </>
    )
  }

  if (isSaleTotalLessThanZero(sale)) {
    messages.push(
      <span key="refund">
        <T
          _str="{amount} refunded"
          _context={SUBHEADER_CONTEXT}
          amount={formatCurrencyValue(
            Math.abs(Number(sale.finals.inclusivePrice))
          )}
        />
      </span>
    )
  }

  if (saleIsExchange(sale)) {
    messages.push(
      <span key="exchange">
        <T
          _str="{count} {count, plural, one {item exchanged} other {items exchanged}}"
          _context={SUBHEADER_CONTEXT}
          count={Math.abs(getTotalQuantityByType(sale, true))}
        />
      </span>
    )
  }
  if (isPartialPaidSale(sale)) {
    if (isLegacyServiceSale(sale.status)) {
      messages.push(
        <span key="legacy-service">
          <T
            _str="{amount} owing on service sale"
            _context={SUBHEADER_CONTEXT}
            amount={formatCurrencyValue(getOutstandingBalance(sale))}
          />
        </span>
      )
    } else {
      messages.push(
        <span key="unfulfilled">
          <T
            _str="{amount} owing on unfulfilled sale"
            _context={SUBHEADER_CONTEXT}
            amount={formatCurrencyValue(getOutstandingBalance(sale))}
          />
        </span>
      )
    }
  }

  if (
    isAccountBalancePaymentPaid(sale.payAccountBalance) &&
    // ABP-only confirmation uses the generic title and no amount subheader copy.
    !isAbpOnlyConfirmationFlow(sale)
  ) {
    messages.push(
      <span key="account-balance">
        <T
          _str="{amount} Account balance payment received"
          _context={SUBHEADER_CONTEXT}
          amount={formatCurrencyValue(Number(sale.payAccountBalance?.amount))}
        />
      </span>
    )
  }

  if (isMarkedAsUnfulfilled(sale.status)) {
    messages.push(
      <span key="paid">
        <T
          _str="{amount} paid"
          _context={SUBHEADER_CONTEXT}
          amount={formatCurrencyValue(getPaymentsTotal(sale.payments))}
        />
      </span>
    )
  }

  if (
    hasOutstandingBalance(sale) &&
    status &&
    CONFIRMED_STATUSES_CLOSED.includes(status)
  ) {
    if (isOnAccount(status)) {
      messages.push(
        <span key="on-account">
          <T _str="On account complete" _context={SUBHEADER_CONTEXT} />
        </span>
      )
    } else {
      messages.push(
        <span key="layaway">
          <T _str="Layaway complete" _context={SUBHEADER_CONTEXT} />
        </span>
      )
    }
  }

  if (
    !isPartialPaidSale(sale) &&
    !isUnpaidSale(sale) &&
    loyaltyManager.canDisplayReceiptLoyalty() &&
    (loyaltyManager.saleHasLoyaltyEarned() ||
      loyaltyManager.saleHasLoyaltyDeducted()) &&
    isValidCustomer(sale.customer) &&
    sale.customer.enable_loyalty
  ) {
    if (loyaltyManager.saleHasLoyaltyEarned()) {
      messages.push(
        <span key="loyalty-earned">
          <T
            _str="Loyalty earned: {amount}"
            _context={SUBHEADER_CONTEXT}
            amount={formatCurrencyValue(
              Math.abs(Number(loyaltyManager.getTotalLoyalty()))
            )}
          />
        </span>
      )
    } else {
      messages.push(
        <span key="loyalty-deducted">
          <T
            _str="{amount} loyalty deducted"
            _context={SUBHEADER_CONTEXT}
            amount={formatCurrencyValue(
              Math.abs(Number(loyaltyManager.getTotalLoyalty()))
            )}
          />
        </span>
      )
    }
  }

  return (
    <>
      {messages.map((message, index) => {
        return index === messages.length - 1 ? <>{message}</> : <>{message}, </>
      })}
    </>
  )
}

export const ConfirmationHeader: FunctionComponent = () => {
  return (
    <div className="sale-confirmation-header">
      <Header type="grand" level="1" className="pro-sale-confirmation-header">
        <HeaderTitle />
      </Header>
      <Header
        type="heading"
        level="1"
        className="pro-sale-confirmation-sub-header vd-pt3"
      >
        <HeaderSubTitle />
      </Header>
    </div>
  )
}
